import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, AlertTriangle, Inbox, Calendar, Send, X, Archive, Clock, AlertCircle, HardHat } from "lucide-react";
import MobPopover from "./MobPopover";
import ScheduleAlerts from "./ScheduleAlerts";
import { sendToDispatchWithPrompt } from "./CardReadiness";
import { smartTitleFromMob } from "../lib/mobTitle";

// --- date helpers ---
const d2 = (n) => String(n).padStart(2, "0");
const fmtIso = (d) => `${d.getFullYear()}-${d2(d.getMonth() + 1)}-${d2(d.getDate())}`;
const parseIso = (s) => { const [y, m, day] = (s || "").split("-").map(Number); return new Date(y, m - 1, day); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(d.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); x.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return x; };
const daysBetween = (a, b) => Math.round((parseIso(b) - parseIso(a)) / 86400000) + 1;
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const SHORT_DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const VIEW_DAYS = { day: 1, week: 7, "2week": 14, "3week": 21, month: 28 };
const VIEW_LABELS = { day: "Day", week: "Week", "2week": "2 Wk", "3week": "3 Wk", month: "Month" };

// --- capacity + budget helpers ---
const budgetBgColor = (pct) => (pct >= 0.95 ? "bg-red-100 text-red-700 border-red-200" : pct > 0.8 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200");
const capBgColor = (p, f, t) => (p > t ? "bg-red-500" : p > f ? "bg-amber-400" : "bg-emerald-500");

function expandMobToDays(m) {
  // Return [iso, ...] covering scheduled_start_date → scheduled_end_date
  // (or fall back to single day / requested_start_date if the mob has no scheduled start yet)
  const start = m.scheduled_start_date || m.requested_start_date || "";
  if (!start) return [];
  const end = m.scheduled_end_date || m.requested_end_date || start;
  const days = Math.max(1, daysBetween(start, end));
  return Array.from({ length: days }, (_, i) => fmtIso(addDays(parseIso(start), i)));
}

function computeMobDurationDays(m) {
  const totalHrs = (Number(m.estimated_duration_hours) || 8) * 1; // treat as total, not per-day
  const crew = Math.max(1, Number(m.num_installers) || 1);
  return Math.max(1, Math.ceil(totalHrs / 10 / crew));
}

export default function ScheduleBoardV2() {
  // Always default to TODAY (forward-looking). View toggle picks a window length;
  // navigation arrows shift by that length.
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [view, setView] = useState("2week");
  const [mobs, setMobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [search, setSearch] = useState("");
  const [dragMobId, setDragMobId] = useState(null);
  // The specific day card that initiated the drag (if any). When a multi-day
  // mob is dragged from one of its day cards, drop = split-on-drag of just
  // that single day. When dragged from the Schedule Pool (no dragDay), drop =
  // schedule the whole mob at the target date.
  const [dragDay, setDragDay] = useState(null);  // { mobId, iso } | null
  // Multi-select set of day cards keyed `${mobId}:${iso}`. Shift+click toggles.
  const [selectedDays2, setSelectedDays2] = useState(new Set());
  // Popover state: open the detailed mob popover when a card is clicked once.
  const [popoverMob, setPopoverMob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poolOpen, setPoolOpen] = useState(true);
  const [futureOpen, setFutureOpen] = useState(false);
  // Edge-resize state: { mobId, side: 'left'|'right', currentIso, anchorStart, anchorEnd }
  const [resize, setResize] = useState(null);
  // Keyboard-delete: the mob currently clicked / selected, primed for Delete or Backspace.
  const [selectedMobId, setSelectedMobId] = useState(null);
  // Double-click selection of a single day in a multi-day span. Highlights and
  // primes Delete/Backspace to trim just that one day (instead of the whole mob).
  // Shape: { mobId, iso }
  const [selectedDay, setSelectedDay] = useState(null);
  // Visual feedback while dragging a card over the Schedule Pool.
  const [poolDragOver, setPoolDragOver] = useState(false);
  // Weekends are hidden by default; the choice is remembered per browser.
  const [showWeekends, setShowWeekends] = useState(
    () => localStorage.getItem("fieldops-show-weekends") === "1",
  );
  const toggleWeekends = () => setShowWeekends((v) => {
    localStorage.setItem("fieldops-show-weekends", v ? "0" : "1");
    return !v;
  });

  const allDates = useMemo(() => Array.from({ length: VIEW_DAYS[view] }, (_, i) => addDays(weekStart, i)), [weekStart, view]);
  const dates = useMemo(() => (showWeekends ? allDates : allDates.filter((d) => !isWeekend(d))), [allDates, showWeekends]);
  const fromIso = fmtIso(allDates[0]);
  const toIso = fmtIso(allDates[allDates.length - 1]);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p, i, a, s, cl] = await Promise.all([
        api.get("/mobilizations"),
        api.get("/projects"),
        api.get("/installers"),
        api.get("/assignments", { params: { date_from: fromIso, date_to: toIso } }),
        api.get("/scope-codes"),
        api.get("/clients"),
      ]);
      setMobs(m.data || []);
      setProjects(p.data || []);
      setInstallers((i.data || []).filter((x) => x.active));
      setAssignments(a.data || []);
      setScopes(s.data || []);
      setClients(cl.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromIso, toIso]);

  // ---- Edge-resize: live mousemove tracks the cell under the cursor; mouseup persists ----
  useEffect(() => {
    if (!resize) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const td = el.closest("td[data-cell-iso]");
      if (!td) return;
      const iso = td.getAttribute("data-cell-iso");
      if (iso && iso !== resize.currentIso) setResize((r) => ({ ...r, currentIso: iso }));
    };
    const onUp = async () => {
      const r = resize;
      setResize(null);
      if (!r) return;
      const m = mobs.find((x) => x.id === r.mobId);
      if (!m) return;
      let newStart = r.anchorStart;
      let newEnd = r.anchorEnd;
      if (r.side === "right") {
        newEnd = r.currentIso > r.anchorStart ? r.currentIso : r.anchorStart;
      } else {
        newStart = r.currentIso < r.anchorEnd ? r.currentIso : r.anchorEnd;
      }
      if (newStart === r.anchorStart && newEnd === r.anchorEnd) return; // no-op
      const days = daysBetween(newStart, newEnd);
      const crew = Math.max(1, Number(m.num_installers) || 1);
      const newHours = days * crew * 8;
      try {
        await api.patch(`/mobilizations/${r.mobId}`, {
          scheduled_start_date: newStart,
          scheduled_end_date: newEnd,
          estimated_duration_hours: newHours,
          status: m.status === "requested" ? "scheduled" : m.status,
        });
        toast.success(`Spans ${days}d · ${newHours}h`);
        load();
      } catch { toast.error("Could not resize"); }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [resize, mobs]); // eslint-disable-line

  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const clientsMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  // Conflicts (computed across all visible assignments + mobs)
  // Returns: { mobIssue: Map<mobId, {undermanned, overmanned}>,
  //           dayDoubleBook: Map<`${installerId}|${iso}`, mobIds[]> }
  const conflicts = useMemo(() => {
    const dayDoubleBook = new Map(); // installer|date -> [mobId,…] (only if >1 distinct mob)
    const perInstDay = new Map();    // installer|date -> Set(mobId)
    for (const a of assignments) {
      if (!a.mobilization_id || !a.installer_id || !a.assigned_date) continue;
      const key = `${a.installer_id}|${a.assigned_date}`;
      if (!perInstDay.has(key)) perInstDay.set(key, new Set());
      perInstDay.get(key).add(a.mobilization_id);
    }
    for (const [k, set] of perInstDay.entries()) {
      if (set.size > 1) dayDoubleBook.set(k, [...set]);
    }
    // Mob-level: required vs assigned (count distinct installers across the
    // mob's day span). Only flag mobs that are AT/PAST the dispatch step —
    // newly-scheduled mobs are expected to have zero assignments while the
    // scheduler is still planning, so painting them all red is just noise.
    const STAFFING_STATUSES = new Set(["ready_to_dispatch", "dispatched", "in_progress", "completed"]);
    const mobIssue = new Map();
    for (const m of mobs) {
      const required = Math.max(1, Number(m.num_installers) || 1);
      const assignedSet = new Set(
        assignments.filter((a) => a.mobilization_id === m.id).map((a) => a.installer_id),
      );
      const assigned = assignedSet.size;
      const isStaffingStage = STAFFING_STATUSES.has(m.status);
      if (assigned === 0 && isStaffingStage) {
        mobIssue.set(m.id, { kind: "unstaffed", required, assigned });
      } else if (assigned > 0 && assigned < required) {
        mobIssue.set(m.id, { kind: "undermanned", required, assigned });
      } else if (assigned > required) {
        mobIssue.set(m.id, { kind: "overmanned", required, assigned });
      }
    }
    return { dayDoubleBook, mobIssue };
  }, [assignments, mobs]);
  // Trade-name → scope code (e.g. "Toilet Room Accessories" → "TRA")
  const scopeMap = useMemo(() => {
    const m = {};
    for (const s of scopes) {
      if (s.trade) m[s.trade.toLowerCase()] = s.code;
      if (s.code) m[s.code.toLowerCase()] = s.code;
    }
    return m;
  }, [scopes]);
  const scopeCodeFor = (mob) => {
    const full = (mob.scope_type || "").split(",")[0].trim();
    if (!full) return "";
    return scopeMap[full.toLowerCase()] || full
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 4)
      .toUpperCase();
  };

  // Mobs that are NOT on the board yet — a request card stays in the Schedule
  // Pool until the scheduler gives it a scheduled date (requested/ABS dates
  // from Clusters 3+5 are proposals, not a placement).
  const poolMobs = useMemo(() => {
    const rank = { urgent: 0, high: 0, normal: 1, low: 2 };
    return mobs
      .filter((m) => !m.scheduled_start_date
        && !["ready_to_dispatch", "dispatched", "completed", "cancelled"].includes(m.status))
      // Default pool order: Start date ascending, then priority (High → Low).
      .sort((a, b) => (a.requested_start_date || "9999-12-31").localeCompare(b.requested_start_date || "9999-12-31")
        || (rank[(a.priority || "normal").toLowerCase()] ?? 1) - (rank[(b.priority || "normal").toLowerCase()] ?? 1)
        || (a.title || "").localeCompare(b.title || ""));
  }, [mobs]);

  // Mobs that ARE on the board (scheduler placed them on a date)
  const onBoardMobs = useMemo(() => mobs.filter((m) => !!m.scheduled_start_date), [mobs]);

  // Future Pipeline (3w+ out)
  const futureCutoffIso = fmtIso(addDays(new Date(), 21));
  const futureMobs = useMemo(
    () => onBoardMobs.filter((m) => (m.scheduled_start_date || m.requested_start_date) > futureCutoffIso).sort((a, b) => (a.scheduled_start_date || "").localeCompare(b.scheduled_start_date || "")),
    [onBoardMobs, futureCutoffIso],
  );

  // Mobs intersecting the visible date range
  const visibleMobs = useMemo(() => {
    return onBoardMobs.filter((m) => {
      const days = expandMobToDays(m);
      return days.some((d) => d >= fromIso && d <= toIso);
    });
  }, [onBoardMobs, fromIso, toIso]);

  // Group visible mobs by project. Projects whose cards are ALL still in the
  // Schedule Pool also get an (empty) row so the scheduler has a drop target.
  const projectRows = useMemo(() => {
    const groups = new Map();
    for (const m of visibleMobs) {
      if (!projectsMap[m.project_id]) continue; // orphan mob — its project was deleted
      if (!groups.has(m.project_id)) groups.set(m.project_id, []);
      groups.get(m.project_id).push(m);
    }
    for (const m of poolMobs) {
      if (!projectsMap[m.project_id]) continue;
      if (!groups.has(m.project_id)) groups.set(m.project_id, []);
    }
    return [...groups.entries()].map(([pid, ms]) => {
      // Stable lane assignment: each mob gets the lowest lane index that doesn't
      // collide with any earlier-starting mob's date span. This guarantees a mob
      // occupies the same horizontal lane across every day it spans, so a 31-unit
      // mob starting Mon stays in lane 0 even when another mob starts Tue.
      const sorted = [...ms].sort((a, b) =>
        ((a.scheduled_start_date || a.requested_start_date) || "").localeCompare(
          (b.scheduled_start_date || b.requested_start_date) || ""
        ) || a.id.localeCompare(b.id)
      );
      const laneEnd = []; // laneEnd[i] = ISO end date occupied in lane i
      const laneByMobId = {};
      for (const m of sorted) {
        const start = (m.scheduled_start_date || m.requested_start_date) || "";
        const end = (m.scheduled_end_date || m.requested_end_date || start) || "";
        let lane = 0;
        while (lane < laneEnd.length && laneEnd[lane] >= start) lane += 1;
        laneEnd[lane] = end;
        laneByMobId[m.id] = lane;
      }
      const numLanes = Math.max(1, laneEnd.length);
      return {
        project: projectsMap[pid] || { id: pid, project_name: "Unknown" },
        mobs: ms,
        laneByMobId,
        numLanes,
      };
    });
  }, [visibleMobs, projectsMap, poolMobs]);

  // Weekly capacity math
  const weeks = useMemo(() => {
    // Chunk visible dates into 7-day rolling windows starting from `dates[0]`.
    // (Day view: 1 chunk of 1 day. Week: 1 chunk of 7. 2Wk: 2 chunks of 7. etc.)
    const chunkSize = view === "day" ? 1 : 7;
    const out = [];
    for (let i = 0; i < allDates.length; i += chunkSize) {
      out.push(allDates.slice(i, i + chunkSize));
    }
    return out;
  }, [allDates, view]);

  const capacity = useMemo(() => {
    const installerCount = installers.filter((i) => i.role === "Installer" || i.role === "Lead").length;
    const foremen = installers.filter((i) => i.role === "Foreman");
    // Per-day capacity assumes a 10h worker day (matches the user's max_hours_per_day standard).
    const installerCapPerDay = installerCount * 10;
    const foremanReservePerDay = foremen.reduce((s, f) => s + 10 * ((f.max_tool_hours_pct || 25) / 100), 0);
    return {
      installerCapPerDay,
      foremanReservePerDay,
    };
  }, [installers]);

  // Per-chunk capacity (sum across the chunk's workdays).
  const chunkCapacity = (chunkDays) => {
    const workdays = chunkDays.filter((d) => !isWeekend(d)).length || chunkDays.length;
    return {
      installerCap: capacity.installerCapPerDay * workdays,
      foremanReserve: capacity.foremanReservePerDay * workdays,
      totalCap: (capacity.installerCapPerDay + capacity.foremanReservePerDay) * workdays,
    };
  };

  const weekPlannedHours = (weekDays) => {
    const set = new Set(weekDays.map(fmtIso));
    return visibleMobs
      .filter((m) => expandMobToDays(m).some((d) => set.has(d)))
      .reduce((s, m) => {
        const d = expandMobToDays(m);
        const overlap = d.filter((x) => set.has(x)).length;
        const totalDays = d.length || 1;
        const portion = overlap / totalDays;
        return s + (Number(m.estimated_duration_hours) || 8) * portion;
      }, 0);
  };

  // Budget rollup per project
  const projectBudgetStats = (pid) => {
    const scheduled = mobs
      .filter((m) => m.project_id === pid && m.status !== "completed")
      .reduce((s, m) => s + (Number(m.estimated_duration_hours) || 8), 0);
    const budget = Number(projectsMap[pid]?.budgeted_hours || 0);
    const pct = budget > 0 ? scheduled / budget : 0;
    return { scheduled, budget, pct };
  };

  // Actions
  const prev = () => setWeekStart((s) => addDays(s, -VIEW_DAYS[view]));
  const next = () => setWeekStart((s) => addDays(s, VIEW_DAYS[view]));
  const today = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setWeekStart(d);
  };
  // View change is purely cosmetic — keep the start date the same so "today" remains the anchor.
  const switchView = (v) => setView(v);

  const scheduleMobOnDate = async (mobId, iso) => {
    const m = mobs.find((x) => x.id === mobId);
    if (!m) return;
    const days = computeMobDurationDays(m);
    const endIso = fmtIso(addDays(parseIso(iso), days - 1));
    try {
      await api.patch(`/mobilizations/${mobId}`, {
        scheduled_start_date: iso,
        scheduled_end_date: endIso,
        status: "scheduled",
      });
      load();
    } catch { toast.error("Could not schedule"); }
  };
  const unscheduleMob = async (mobId) => {
    try {
      await api.patch(`/mobilizations/${mobId}`, { scheduled_start_date: "", scheduled_end_date: "", status: "requested" });
      load();
    } catch { toast.error("Could not unschedule"); }
  };
  const mobsById = useMemo(() => Object.fromEntries((mobs || []).map((m) => [m.id, m])), [mobs]);
  const sendToDispatch = (ids) => sendToDispatchWithPrompt(ids, load);
  const _legacySendToDispatch = async (ids) => {
    try {
      await api.post("/mobilizations/bulk-status", { ids, status: "ready_to_dispatch" });
      toast.success(`Sent ${ids.length} to Dispatch`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.startsWith("Lineout required")) {
        toast.error("Lineout required", {
          description: 'Add the "Parking and Site Logistics" note on the card before dispatching.',
        });
      } else {
        toast.error(detail || "Could not send");
      }
    }
  };

  const onCellDrop = async (e, iso, projectId) => {
    e.preventDefault();
    if (!dragMobId) return;
    const mobId = dragMobId;
    const day = dragDay;
    const dragged = mobs.find((x) => x.id === mobId);
    if (projectId && dragged && dragged.project_id !== projectId) {
      setDragMobId(null);
      setDragDay(null);
      toast.error("Drop this card on its own job row");
      return;
    }
    setDragMobId(null);
    setDragDay(null);
    // Multi-day day-card drag → split-on-drag (single day or multi-select group)
    if (day && day.mobId === mobId) {
      const m = mobs.find((x) => x.id === mobId);
      const totalDays = m ? expandMobToDays(m).length : 1;
      // If a multi-select group is active and contains the drag-origin day,
      // move the whole group (each selected day moves to target preserving
      // relative offset from the drag-origin day).
      const groupKey = `${day.mobId}:${day.iso}`;
      const group = selectedDays2.has(groupKey) && selectedDays2.size > 1
        ? [...selectedDays2].map((k) => { const [mid, dIso] = k.split(":"); return { mobId: mid, iso: dIso }; })
        : null;
      if (group) {
        // sort by iso ascending; anchor is the dragged day
        const anchor = parseIso(day.iso);
        const target = parseIso(iso);
        for (const g of group) {
          const offset = Math.round((parseIso(g.iso) - anchor) / 86400000);
          const newTarget = fmtIso(addDays(target, offset));
          try { await api.post(`/mobilizations/${g.mobId}/split-day`, { day_iso: g.iso, target_iso: newTarget }); }
          catch { /* tolerated */ }
        }
        toast.success(`Moved ${group.length} day${group.length === 1 ? "" : "s"}`);
        setSelectedDays2(new Set());
        load();
        return;
      }
      if (totalDays > 1) {
        try {
          await api.post(`/mobilizations/${mobId}/split-day`, { day_iso: day.iso, target_iso: iso });
          toast.success("Day split");
          load();
        } catch { toast.error("Could not split day"); }
        return;
      }
    }
    // Default: schedule the whole mob (from pool, or 1-day mob)
    scheduleMobOnDate(mobId, iso);
  };
  const onPoolDrop = (e) => {
    e.preventDefault();
    setPoolDragOver(false);
    if (!dragMobId) return;
    unscheduleMob(dragMobId);
    setDragMobId(null);
    setDragDay(null);
  };

  // Trim a single day from a mob's scheduled span. If the day is the last
  // remaining day, the entire mob is sent back to the Schedule Pool.
  const trimMobDay = async (mobId, iso) => {
    const m = mobs.find((x) => x.id === mobId);
    if (!m) return;
    const start = m.scheduled_start_date || m.requested_start_date;
    const end = m.scheduled_end_date || m.requested_end_date || start;
    if (!start) return;
    const allDays = expandMobToDays(m);
    const idx = allDays.indexOf(iso);
    if (idx === -1) return;
    const totalDays = allDays.length;
    // Single-day mob → whole mob back to pool
    if (totalDays <= 1) {
      await unscheduleMob(mobId);
      toast.success(`"${m.scope_type || m.title || "mob"}" sent back to Schedule Pool`);
      return;
    }
    let newStart = start;
    let newEnd = end;
    if (idx === 0) newStart = allDays[1];
    else if (idx === totalDays - 1) newEnd = allDays[totalDays - 2];
    else {
      // Middle-day removal would require splitting the mob — not supported yet.
      toast.error("Can only trim the first or last day of a span. Resize the edges to remove middle days.");
      return;
    }
    const newDays = daysBetween(newStart, newEnd);
    const crew = Math.max(1, Number(m.num_installers) || 1);
    const newHours = newDays * crew * 10;
    try {
      await api.patch(`/mobilizations/${mobId}`, {
        scheduled_start_date: newStart,
        scheduled_end_date: newEnd,
        estimated_duration_hours: newHours,
      });
      toast.success(`Removed ${iso}; spans ${newDays}d`);
      load();
    } catch { toast.error("Could not trim day"); }
  };

  const deleteMob = (m) => {
    toast(`Remove "${m.scope_type || m.title || "mob"}" from schedule?`, {
      duration: 8000,
      action: {
        label: "Remove",
        onClick: async () => {
          try {
            await api.delete(`/mobilizations/${m.id}`);
            toast.success("Removed");
            load();
          } catch { toast.error("Delete failed"); }
        },
      },
      cancel: { label: "Cancel" },
      classNames: { actionButton: "!bg-red-600 !text-white hover:!bg-red-700" },
    });
  };

  // ---- Keyboard-delete on the currently selected mob card (or single day) ----
  // - selectedDay is set: Delete trims just that one day (or sends mob to pool if last day).
  // - selectedMobId is set: Delete sends the whole mob BACK TO THE SCHEDULE POOL
  //   (clears scheduled dates + flips status to "requested"). The mob itself is not
  //   destroyed so past references remain intact.
  useEffect(() => {
    if (!selectedMobId && !selectedDay) return;
    const onKey = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      e.preventDefault();
      if (selectedDay) {
        trimMobDay(selectedDay.mobId, selectedDay.iso);
        setSelectedDay(null);
        return;
      }
      const m = mobs.find((x) => x.id === selectedMobId);
      if (!m) return;
      unscheduleMob(m.id);
      toast.success(`"${m.scope_type || m.title || "mob"}" sent back to Schedule Pool`);
      setSelectedMobId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedMobId, selectedDay, mobs]); // eslint-disable-line

  // Clear selection when the user clicks anywhere that isn't a mob card.
  useEffect(() => {
    const onClick = (e) => {
      if (!selectedMobId && !selectedDay && selectedDays2.size === 0) return;
      if (!e.target.closest('[data-testid^="sched-v2-cell-card-"]') &&
          !e.target.closest('[data-testid^="sched-v2-cont-card-"]')) {
        setSelectedMobId(null);
        setSelectedDay(null);
        setSelectedDays2(new Set());
      }
    };
    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [selectedMobId, selectedDay, selectedDays2]);

  const searchFilter = (m) => {
    if (!search) return true;
    const p = projectsMap[m.project_id];
    const blob = [p?.project_name, p?.job_number, m.scope_type, m.title, m.building].join(" ").toLowerCase();
    return blob.includes(search.toLowerCase());
  };

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[640px] gap-3">
      {/* Left panel (collapsible) */}
      <aside className={`${poolOpen || futureOpen ? "w-[280px]" : "w-[44px]"} shrink-0 border border-gray-200 bg-white rounded-lg flex flex-col overflow-hidden transition-all`}>
        {!(poolOpen || futureOpen) ? (
          <button onClick={() => setPoolOpen(true)} className="p-2 flex flex-col items-center gap-2 h-full text-gray-500 hover:bg-gray-50" data-testid="sched-v2-pool-open">
            <Inbox className="w-4 h-4" />
            <span className="text-[10px] rotate-180 [writing-mode:vertical-rl]">Schedule Alerts</span>
          </button>
        ) : (
          <>
            {/* SCH-1 — Schedule Alerts replaces the Schedule Pool: a list, not a drop target. */}
            <ScheduleAlerts mobsById={mobsById} onOpenMob={(m) => setPopoverMob(mobsById[m.id] || m)}
                            onDragMob={setDragMobId} onChanged={load} />

            {/* Future (3w+) */}
            <header className="px-3 py-2 border-b border-t border-gray-200 bg-gray-50/60 flex items-center cursor-pointer" onClick={() => setFutureOpen(!futureOpen)}>
              <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">On the Board (3w+)</h3>
                <p className="text-[10px] text-gray-500">{futureMobs.length} mobs</p>
              </div>
              <span className="text-xs text-gray-400">{futureOpen ? "–" : "+"}</span>
            </header>
            {futureOpen && (
              <div className="max-h-64 overflow-y-auto p-2 space-y-1 bg-gray-50/30">
                {futureMobs.length === 0 && <div className="text-[11px] text-gray-400 text-center py-3">Nothing 3+ weeks out.</div>}
                {futureMobs.map((m) => {
                  const p = projectsMap[m.project_id];
                  const start = m.scheduled_start_date || m.requested_start_date;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setWeekStart(startOfWeek(parseIso(start)))}
                      className="w-full text-left border border-gray-200 bg-white rounded px-2 py-1.5 hover:border-blue-400 text-[11px]"
                    >
                      <div className="font-semibold text-gray-900 truncate">{p?.project_name || m.title}</div>
                      <div className="text-gray-500 text-[10px]">{start} · {(m.scope_type || "").split(",")[0]}</div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="px-2 py-2 border-t border-gray-100">
              <Input data-testid="sched-v2-search" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-7 text-xs" />
            </div>
          </>
        )}
      </aside>

      {/* Main board */}
      <div className="flex-1 border border-gray-200 bg-white rounded-lg flex flex-col overflow-hidden">
        <header className="px-3 py-2 border-b border-gray-200 bg-gray-50/60 flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={prev} className="h-7 w-7 p-0"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={today} className="h-7 text-xs">Today</Button>
          <Button variant="outline" size="sm" onClick={next} className="h-7 w-7 p-0"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-gray-800 ml-1">
            {allDates[0].toLocaleDateString("en", { month: "short", day: "numeric" })} – {allDates[allDates.length - 1].toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" })}
          </span>
          <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none" data-testid="sched-v2-weekend-label">
            <input
              type="checkbox"
              checked={showWeekends}
              onChange={toggleWeekends}
              className="w-3.5 h-3.5"
              data-testid="sched-v2-weekend-toggle"
            />
            Show weekends
          </label>
          <div className="ml-auto inline-flex items-center border border-gray-300 rounded-md bg-white overflow-hidden" data-testid="sched-v2-view-toggle">
            {Object.keys(VIEW_DAYS).map((v) => (
              <button key={v} data-testid={`sched-v2-view-${v}`} onClick={() => switchView(v)} className={`px-2 h-7 text-xs border-l border-gray-300 first:border-l-0 ${view === v ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}>{VIEW_LABELS[v]}</button>
            ))}
          </div>
        </header>

        {/* Grid */}
        <div className="flex-1 overflow-auto" data-testid="sched-v2-grid-scroll">
          <table className="text-xs border-collapse w-full">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className="bg-gray-100 border-b border-r border-gray-200 text-left px-2 py-1.5 sticky left-0" style={{ minWidth: 240, zIndex: 40 }}>
                  <span className="text-[10px] uppercase text-gray-500 font-medium">Job</span>
                </th>
                {dates.map((d) => (
                  <th key={fmtIso(d)} className={`border-b border-r border-gray-200 px-1 py-1.5 text-center font-medium ${isWeekend(d) ? "bg-gray-200 text-gray-500" : "bg-gray-50 text-gray-700"}`} style={{ minWidth: view === "day" ? 640 : view === "week" ? 213 : 160 }}>
                    <div className="text-[10px] uppercase tracking-wider">{SHORT_DAY[d.getDay()]}</div>
                    <div className="text-[11px]">{d.getMonth() + 1}/{d.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projectRows.map(({ project, mobs: pmobs, laneByMobId, numLanes }) => {
                const stats = projectBudgetStats(project.id);
                return (
                  <tr key={project.id} className="hover:bg-blue-50/30">
                    <td className="border-b border-r border-gray-200 px-2 py-2 sticky left-0 bg-white align-top" style={{ zIndex: 30 }}>
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/projects/${project.id}`}
                            className="font-semibold text-blue-700 hover:text-blue-900 hover:underline text-[12px] truncate block"
                            title="Open job · FRS"
                            data-testid={`sched-v2-project-link-${project.id}`}
                          >
                            {project.project_name}
                          </Link>
                          <div className="text-[10px] text-gray-500 flex items-center gap-1.5 truncate">
                            {project.job_number && <span>{project.job_number}</span>}
                            {clientsMap[project.client_id]?.name && (
                              <>
                                {project.job_number && <span className="text-gray-300">·</span>}
                                <span className="text-blue-600 truncate" title="Client">{clientsMap[project.client_id].name}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {stats.budget > 0 ? (
                          <div className={`shrink-0 inline-flex items-center gap-1 text-[10px] rounded-full border px-1.5 py-0.5 font-medium ${budgetBgColor(stats.pct)}`} data-testid={`sched-v2-budget-${project.id}`}>
                            {Math.round(stats.scheduled)}/{stats.budget}h · {Math.round(stats.pct * 100)}%
                          </div>
                        ) : (
                          <div className="shrink-0 text-[10px] text-gray-400">{Math.round(stats.scheduled)}h</div>
                        )}
                      </div>
                    </td>
                    {dates.map((d) => {
                      const iso = fmtIso(d);
                      const onThisDay = pmobs.filter((m) => expandMobToDays(m).includes(iso));
                      const wk = isWeekend(d);
                      // Live highlight while user is edge-dragging this project's mob
                      const inResize =
                        resize &&
                        pmobs.some((m) => m.id === resize.mobId) &&
                        ((resize.side === "right" && iso > resize.anchorStart && iso <= resize.currentIso) ||
                          (resize.side === "left" && iso < resize.anchorEnd && iso >= resize.currentIso));
                      // Build N fixed lane slots so each mob keeps the same vertical
                      // position across every day in its span.
                      const lanes = Array(numLanes).fill(null);
                      for (const m of onThisDay) {
                        const lane = laneByMobId[m.id] ?? 0;
                        lanes[lane] = m;
                      }
                      return (
                        <td
                          key={iso}
                          data-testid={`sched-v2-cell-${project.id}-${iso}`}
                          data-cell-iso={iso}
                          data-cell-project={project.id}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => onCellDrop(e, iso, project.id)}
                          className={`border-b border-r border-gray-200 p-0.5 align-top ${wk ? "bg-gray-50" : ""} ${inResize ? "bg-blue-100/70" : ""}`}
                          style={{ minWidth: view === "day" ? 640 : view === "week" ? 213 : 160, minHeight: Math.max(60, 56 * numLanes + 6) }}
                        >
                          {lanes.map((m, laneIdx) => {
                            if (!m) {
                              // Empty lane keeps the row height aligned with neighbors.
                              return <div key={`empty-${laneIdx}`} style={{ height: 52, marginBottom: 4 }} />;
                            }
                            const startIso = m.scheduled_start_date || m.requested_start_date;
                            const endIso = m.scheduled_end_date || m.requested_end_date || startIso;
                            const allDays = expandMobToDays(m);
                            const dayIndex = allDays.indexOf(iso);
                            const totalDays = allDays.length;
                            const isResizing = resize && resize.mobId === m.id;
                            const isSelected = selectedMobId === m.id;
                            const isDaySelected = selectedDay && selectedDay.mobId === m.id && selectedDay.iso === iso;
                            const isInGroup = selectedDays2.has(`${m.id}:${iso}`);
                            const mobIssue = conflicts.mobIssue.get(m.id) || null;
                            // Day-level double-book? (any installer on this mob is also on another mob today)
                            const mobAssigneesToday = assignments
                              .filter((a) => a.mobilization_id === m.id && a.assigned_date === iso)
                              .map((a) => a.installer_id);
                            const hasDayDoubleBook = mobAssigneesToday.some(
                              (iid) => conflicts.dayDoubleBook.has(`${iid}|${iso}`),
                            );
                            return (
                              <CellCard
                                key={`${m.id}-${iso}`}
                                mob={m}
                                iso={iso}
                                project={project}
                                scopeCode={scopeCodeFor(m)}
                                dayIndex={dayIndex}
                                totalDays={totalDays}
                                drag={setDragMobId}
                                setDragDay={setDragDay}
                                isResizing={isResizing}
                                isSelected={isSelected}
                                isDaySelected={isDaySelected}
                                isInGroup={isInGroup}
                                mobIssue={mobIssue}
                                hasDayDoubleBook={hasDayDoubleBook}
                                onSelect={(e) => {
                                  if (e && (e.shiftKey || e.metaKey || e.ctrlKey)) {
                                    // Toggle group membership (no popover)
                                    const k = `${m.id}:${iso}`;
                                    const next = new Set(selectedDays2);
                                    if (next.has(k)) next.delete(k); else next.add(k);
                                    setSelectedDays2(next);
                                    setSelectedMobId(null);
                                    setSelectedDay(null);
                                    return;
                                  }
                                  // Plain click: open detailed popover
                                  setPopoverMob(m);
                                  setSelectedDays2(new Set());
                                  setSelectedDay(null);
                                  setSelectedMobId(null);
                                }}
                                onSelectDay={() => { setSelectedDay({ mobId: m.id, iso }); setSelectedMobId(null); setSelectedDays2(new Set()); }}
                                onSend={() => sendToDispatch([m.id])}
                                onDelete={() => deleteMob(m)}
                                onResizeStart={(side) => {
                                  setResize({
                                    mobId: m.id,
                                    side,
                                    anchorStart: startIso,
                                    anchorEnd: endIso,
                                    currentIso: side === "right" ? endIso : startIso,
                                  });
                                }}
                              />
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {projectRows.length === 0 && !loading && (
                <tr><td colSpan={dates.length + 1} className="px-3 py-16 text-center text-gray-500">No scheduled work in this view. Submit a request from the FRS tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Capacity meters per chunk (per day in Day view, per week otherwise) */}
        <div className="border-t-2 border-gray-200 bg-gray-50/60 px-3 py-2 flex gap-2 overflow-x-auto" data-testid="sched-v2-capacity">
          {weeks.map((weekDays, i) => {
            const planned = weekPlannedHours(weekDays);
            const { installerCap, foremanReserve, totalCap } = chunkCapacity(weekDays);
            const pct = totalCap > 0 ? planned / totalCap : 0;
            const barColor = capBgColor(planned, installerCap, totalCap);
            const label = planned > totalCap ? "OVER" : planned > installerCap ? "NEEDS FOREMEN" : "OK";
            const isDay = weekDays.length === 1;
            const dateLabel = isDay
              ? weekDays[0].toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" })
              : weekDays[0].toLocaleDateString("en", { month: "short", day: "numeric" });
            return (
              <div key={i} className="flex-1 min-w-[160px] border border-gray-200 bg-white rounded-md p-2" data-testid={`sched-v2-cap-week-${i}`}>
                <div className="text-[10px] text-gray-500 uppercase">{dateLabel} · {label}</div>
                <div className="text-xs font-semibold text-gray-900 mt-0.5">{Math.round(planned)} / {Math.round(totalCap)} h</div>
                <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1 overflow-hidden">
                  <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct * 100)}%` }}></div>
                </div>
                <div className="text-[10px] text-gray-500 mt-1">
                  Installers {Math.round(installerCap)}h + Foremen {Math.round(foremanReserve)}h
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed mob popover (opens on single-click of any cell card) */}
      <MobPopover
        open={!!popoverMob}
        onOpenChange={(o) => { if (!o) setPopoverMob(null); }}
        mob={popoverMob}
        project={popoverMob ? projectsMap[popoverMob.project_id] : null}
        scopeCode={popoverMob ? scopeCodeFor(popoverMob) : ""}
        onChanged={(patch) => {
          // Optimistic local merge so the card updates instantly
          if (!popoverMob) return;
          setMobs((cur) => cur.map((m) => m.id === popoverMob.id ? { ...m, ...patch } : m));
          setPopoverMob((cur) => cur ? { ...cur, ...patch } : cur);
        }}
        onDispatch={() => sendToDispatch([popoverMob.id])}
        onDelete={() => deleteMob(popoverMob)}
      />
    </div>
  );
}

// ---- sub-components ----
function PoolCard({ mob, project, scopeMap, drag, budgetStats, noDate, onOpen, onSend }) {
  const { title: cardTitle } = smartTitleFromMob(mob, scopeMap || {});
  const hours = Math.round(Number(mob.estimated_duration_hours) || 8);
  const insts = mob.num_installers || 1;
  const units = Number(mob.total_quantity) || 0;
  const pct = budgetStats?.budget > 0 ? Math.round(budgetStats.pct * 100) : null;
  const lineoutBlocked = !mob.lineout_ready;
  // Compact line of meta — only joins pieces that exist.
  const metaPieces = [
    project?.job_number,
    cardTitle,
    units ? `${units}u` : "",
    `${insts}I · ${hours}h`,
  ].filter(Boolean);
  return (
    <div
      draggable
      onDragStart={() => drag(mob.id)}
      onDragEnd={() => drag(null)}
      onClick={() => onOpen && onOpen()}
      data-testid={`sched-v2-pool-card-${mob.id}`}
      className={`border rounded-md px-1.5 py-1 text-xs hover:shadow-sm cursor-grab active:cursor-grabbing ${
        mob.is_punchlist ? "bg-orange-50 border-orange-300 hover:border-orange-500" : "bg-white border-gray-200 hover:border-blue-400"
      }`}
    >
      {/* Row 1 — project name + status badges (NO DATE / Punchlist / Budget %) */}
      <div className="flex items-center justify-between gap-1">
        <div className="font-semibold truncate flex-1 min-w-0 text-[12px]" title={project?.project_name || mob.title}>
          {project?.id ? (
            <Link
              to={`/projects/${project.id}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="text-blue-700 hover:text-blue-900 hover:underline"
              title="Open job · FRS"
              data-testid={`sched-v2-pool-link-${mob.id}`}
            >
              {project.project_name || mob.title}
            </Link>
          ) : (
            <span className="text-gray-900">{mob.title}</span>
          )}
          {mob.is_punchlist && <span className="ml-1 text-[8px] uppercase font-bold text-orange-700 align-middle">P</span>}
          {mob.is_auto_request && (
            <span
              className="ml-1 text-[8px] uppercase font-bold text-violet-700 bg-violet-100 border border-violet-200 rounded px-1 align-middle"
              title="Auto-requested — Ready work starting inside the auto-request window"
              data-testid={`sched-v2-pool-auto-${mob.id}`}
            >
              auto
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {pct !== null && (
            <span className={`text-[9px] rounded-full border px-1 ${budgetBgColor(budgetStats.pct)}`} title="Budget used">{pct}%</span>
          )}
          {noDate && (
            <span title="No requested date" className="inline-flex items-center text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">
              <AlertTriangle className="w-2.5 h-2.5" />
            </span>
          )}
        </div>
      </div>
      {/* Row 2 — combined meta (job # · scope/title · units · installers · hours) */}
      <div
        className="text-[10px] text-gray-600 truncate flex items-center gap-1.5"
        title={metaPieces.join(" · ")}
        data-testid={`sched-v2-pool-meta-${mob.id}`}
      >
        {project?.job_number && <span className="text-gray-500">{project.job_number}</span>}
        <span className="font-mono font-semibold text-gray-900" data-testid={`sched-v2-pool-title-${mob.id}`}>{cardTitle}</span>
        {units > 0 && <span className="text-gray-500 truncate" data-testid={`sched-v2-pool-units-${mob.id}`}>{units}u</span>}
        <span className="ml-auto tabular-nums text-gray-700 shrink-0" data-testid={`sched-v2-pool-crew-${mob.id}`}>
          {insts} inst · {hours}h
        </span>
      </div>
      {lineoutBlocked && (
        <div
          className="mt-1 text-[9px] uppercase font-bold text-red-700 bg-red-50 border border-red-300 rounded px-1 py-0.5 inline-block"
          title='Add the "Parking and Site Logistics" lineout note before this card can be dispatched'
          data-testid={`sched-v2-pool-lineout-required-${mob.id}`}
        >
          Lineout required
        </div>
      )}
      {mob.special_notes && (
        <div
          className="mt-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 line-clamp-2"
          title={mob.special_notes}
          data-testid={`sched-v2-pool-special-notes-${mob.id}`}
        >
          {mob.special_notes}
        </div>
      )}
      {/* Row 3 — single inline send button (icon + text, half-height) */}
      <Button
        size="sm"
        variant="outline"
        className="w-full h-5 mt-1 text-[10px] leading-none"
        onClick={(e) => { e.stopPropagation(); onSend(); }}
        data-testid={`sched-v2-send-${mob.id}`}
      >
        <Send className="w-3 h-3 mr-1" /> Send to Dispatch
      </Button>
    </div>
  );
}

function CellCard({
  mob, iso, project, drag, setDragDay, onSend, onResizeStart, onDelete, onSelect, onSelectDay,
  isResizing, isSelected, isDaySelected, isInGroup, scopeCode, dayIndex, totalDays,
  mobIssue, hasDayDoubleBook,
}) {
  const isReady = mob.status === "ready_to_dispatch";
  const isFirst = dayIndex === 0;
  const isLast = dayIndex === totalDays - 1;
  const hrsTotal = Math.round(Number(mob.estimated_duration_hours) || 0);
  // Running cumulative hours so each day in the span shows e.g. 10/30, 20/30, 30/30.
  const cumulativeNow = totalDays > 0 ? Math.round((hrsTotal * (dayIndex + 1)) / totalDays) : hrsTotal;
  // Build a single conflict descriptor (priority: double-book > unstaffed > under > over)
  let conflict = null;
  if (hasDayDoubleBook) {
    conflict = { color: "bg-red-100 border-red-400 text-red-800", label: "DOUBLE-BOOK", title: "An installer on this mob is also assigned to another mob on this day" };
  } else if (mobIssue?.kind === "unstaffed") {
    conflict = {
      color: "bg-red-100 border-red-400 text-red-800",
      iconOnly: true,
      Icon: HardHat,
      title: `No worker scheduled · mob requires ${mobIssue.required} installer${mobIssue.required === 1 ? "" : "s"}`,
    };
  } else if (mobIssue?.kind === "undermanned") {
    conflict = { color: "bg-amber-100 border-amber-400 text-amber-800", label: `−${mobIssue.required - mobIssue.assigned}`, title: `Mob requires ${mobIssue.required} installers; ${mobIssue.assigned} assigned` };
  } else if (mobIssue?.kind === "overmanned") {
    conflict = { color: "bg-purple-100 border-purple-400 text-purple-800", label: `+${mobIssue.assigned - mobIssue.required}`, title: `Mob requires ${mobIssue.required} installers; ${mobIssue.assigned} assigned` };
  }
  const ring = isInGroup
    ? "ring-2 ring-emerald-500 bg-emerald-50"
    : isDaySelected
    ? "ring-2 ring-amber-500 bg-amber-50"
    : isSelected
    ? "ring-2 ring-red-500"
    : isResizing
    ? "ring-2 ring-blue-500"
    : conflict && conflict.label === "DOUBLE-BOOK"
    ? "ring-2 ring-red-500"
    : conflict && conflict.iconOnly
    ? "ring-1 ring-red-400"
    : "";
  // Smart Title (line 1) + Units (line 2). The mob already stores
  // building/floor/area uniform-only; pass scopeCode (resolved by parent) so
  // the helper picks it up when the saved mob doesn't yet have scope_code.
  const { title: cardTitle, units: smartUnits } = smartTitleFromMob(
    { ...mob, scope_code: mob.scope_code || scopeCode },
  );
  const instCount = mob.num_installers || 1;
  const peopleStr = `${instCount} Inst`;
  return (
    <div
      draggable={!isResizing}
      onDragStart={(e) => {
        if (isResizing) { e.preventDefault(); return; }
        drag(mob.id);
        setDragDay && setDragDay({ mobId: mob.id, iso });
      }}
      onDragEnd={() => { drag(null); setDragDay && setDragDay(null); }}
      onClick={(e) => { e.stopPropagation(); onSelect && onSelect(e); }}
      onDoubleClick={(e) => { e.stopPropagation(); onSelectDay && onSelectDay(); }}
      data-testid={`sched-v2-cell-card-${mob.id}-${dayIndex}`}
      className={`relative rounded border px-1 py-1 text-[10px] mb-1 hover:shadow-sm group h-[52px] flex flex-col overflow-hidden ${ring} ${
        mob.is_punchlist
          ? "bg-orange-100 border-orange-400 text-orange-900"
          : isReady
          ? "bg-purple-50 border-purple-300 text-purple-900"
          : "bg-blue-50 border-blue-300 text-blue-900"
      } ${isResizing ? "" : "cursor-grab"}`}
      title={`${project.project_name} · ${mob.scope_type || ""}${mob.is_punchlist ? " · PUNCHLIST" : ""} · ${peopleStr} · ${cumulativeNow}/${hrsTotal}h cumulative · click=open details · dbl-click=select 1 day · Shift-click=add to group`}
    >
      {isFirst && <ResizeHandle side="left" mobId={mob.id} onStart={onResizeStart} />}
      <div className="flex items-start gap-1 px-2.5 flex-1 min-h-0">
        <div className="flex-1 min-w-0 leading-tight">
          <div
            className="font-mono font-semibold text-[11px] truncate"
            data-testid={`sched-v2-cell-title-${mob.id}-${dayIndex}`}
            style={{ wordBreak: "normal", overflowWrap: "break-word" }}
          >
            {cardTitle}
            {mob.is_auto_request && (
              <span
                className="ml-1 text-[8px] uppercase font-bold text-violet-700 bg-violet-100 border border-violet-300 rounded px-0.5 align-middle"
                title="Auto-requested"
                data-testid={`sched-v2-cell-auto-${mob.id}-${dayIndex}`}
              >
                A
              </span>
            )}
          </div>
          {smartUnits && (
            <div className="text-[9px] text-gray-700 truncate" data-testid={`sched-v2-cell-units-${mob.id}-${dayIndex}`}>
              {smartUnits}
            </div>
          )}
        </div>
        {isFirst && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onSend(); }}
              title="Send to Dispatch"
              className="text-purple-700 hover:text-purple-900 opacity-60 group-hover:opacity-100 shrink-0"
              data-testid={`sched-v2-cell-send-${mob.id}`}
            >
              <Send className="w-2.5 h-2.5" />
            </button>
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Remove from schedule"
                className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                data-testid={`sched-v2-cell-delete-${mob.id}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </>
        )}
      </div>
      <div className="text-[9px] text-gray-600 truncate px-2.5 shrink-0">
        {peopleStr} · {cumulativeNow}/{hrsTotal} h
      </div>
      {isLast && <ResizeHandle side="right" mobId={mob.id} onStart={onResizeStart} />}
      {/* Conflict badge — bottom-right corner. Renders only on the first day
          of the span so it doesn't duplicate across a multi-day mob. */}
      {isFirst && conflict && (
        <span
          title={conflict.title}
          data-testid={`sched-v2-conflict-${mob.id}`}
          className={`absolute -bottom-1 right-2 z-10 inline-flex items-center gap-0.5 text-[8px] font-bold px-1 py-0.5 rounded border ${conflict.color}`}
        >
          {conflict.iconOnly ? (
            <conflict.Icon className="w-3 h-3" strokeWidth={2.5} />
          ) : (
            <>
              <AlertCircle className="w-2 h-2" />
              {conflict.label}
            </>
          )}
        </span>
      )}
    </div>
  );
}

// Shared edge-resize handle. 12px-wide hit zone, always-visible 2px guide, brighter on hover.
function ResizeHandle({ side, mobId, onStart }) {
  const sideClass = side === "right" ? "-right-1" : "-left-1";
  return (
    <div
      onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); onStart && onStart(side); }}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
      data-testid={`sched-v2-resize-${side[0]}-${mobId}`}
      className={`absolute ${sideClass} top-0 bottom-0 w-3 cursor-ew-resize z-20 flex items-center justify-center`}
      title={side === "right" ? "Drag right to extend later" : "Drag left to extend earlier"}
    >
      <div className="w-[3px] h-3/4 rounded-full bg-blue-500/50 group-hover:bg-blue-600 transition-colors" />
    </div>
  );
}
